import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArtworkPreview, humanize, studioButton, Template } from './StudioComponents'

export function CreateFilmWizard({
  open,
  onOpenChange,
  templates,
  templateId,
  onSelect,
  onSubmit,
  busy,
  error,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  templates: Template[]
  templateId: string
  onSelect: (id: string) => void
  onSubmit: () => Promise<boolean>
  busy: boolean
  error?: string
}) {
  const [step, setStep] = useState(0)
  const [confirmed, setConfirmed] = useState(false)
  const submitting = useRef(false)
  const template = templates.find((t) => t.id === templateId)
  useEffect(() => {
    if (open) {
      setStep(0)
      setConfirmed(false)
    }
  }, [open])
  async function render() {
    if (submitting.current || !confirmed || !template) return
    submitting.current = true
    try {
      if (await onSubmit()) onOpenChange(false)
    } finally {
      submitting.current = false
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!busy) onOpenChange(value)
      }}
    >
      <DialogContent className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl flex-col overflow-hidden rounded-xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Create a film</DialogTitle>
          <DialogDescription>
            Use an existing world template to render a private draft. Fresh artwork discovery is in
            Candidate planning.
          </DialogDescription>
        </DialogHeader>
        <ol aria-label="Creation steps" className="flex gap-2 text-xs">
          {['Artwork & format', 'Story & source', 'Confirm render'].map((name, i) => (
            <li
              key={name}
              aria-current={step === i ? 'step' : undefined}
              className={`flex-1 rounded border p-2 ${step === i ? 'bg-muted font-semibold' : 'text-muted-foreground'}`}
            >
              {i + 1}. {name}
            </li>
          ))}
        </ol>
        {error && (
          <p role="alert" className="rounded border p-3 text-sm">
            {error}
          </p>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {step === 0 && (
            <div className="space-y-3">
              <h2 className="font-medium">Choose artwork and format</h2>
              {!templates.length && (
                <p>
                  No templates are available. Close this dialog and check service configuration.
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    aria-pressed={templateId === t.id}
                    className={`space-y-2 rounded-xl border p-3 text-left focus-visible:ring-2 focus-visible:ring-ring ${templateId === t.id ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted'}`}
                    onClick={() => {
                      onSelect(t.id)
                      setConfirmed(false)
                    }}
                  >
                    <ArtworkPreview title={t.title} previews={t.previews} />
                    <span className="block font-medium">{t.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t.recipe.artist || 'Artist not recorded'} · {humanize(t.format)} ·{' '}
                      {t.recipe.durationSec}s
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && template && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">{template.title}</h2>
              <p className="text-sm">
                {template.recipe.artist} · {humanize(template.format)} · Vertical 9:16 ·{' '}
                {template.recipe.durationSec}s
              </p>
              <p className="rounded border p-3 text-sm">
                Daily visits use music only. Residency films narrate only the poem; closing artist
                details stay readable. All renders require film review before release.
              </p>
              <p className="text-xs text-muted-foreground">
                {template.identityGaps.join(' · ') || 'Catalogue source identities verified.'}
              </p>
              <details>
                <summary className="min-h-11 cursor-pointer py-2">
                  Grounded brief and storyboard
                </summary>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">
                  {JSON.stringify(
                    { preamble: template.recipe.preamble, shots: template.recipe.shots },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          )}
          {step === 2 && template && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">Render {template.title}</h2>
              <p className="text-sm">
                {humanize(template.format)} · {template.recipe.durationSec}s · Private draft
              </p>
              <p className="rounded border p-3 text-sm">
                No new external generation calls. GCP compute and storage are billed separately, not
                free. Full-quality CPU renders may take about an hour. You can leave and return to
                the job.
              </p>
              <label className="flex min-h-11 items-start gap-2 rounded border p-3 text-sm">
                <input
                  className="mt-1"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I confirm this private render. This does not approve a film, promote a customer
                world, or post to Instagram.
              </label>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap justify-between gap-2 border-t pt-4">
          <button
            className={studioButton}
            disabled={busy}
            onClick={() => (step ? setStep(step - 1) : onOpenChange(false))}
          >
            {step ? 'Back' : 'Cancel'}
          </button>
          {step < 2 ? (
            <button
              className={`${studioButton} bg-primary text-primary-foreground hover:bg-primary/90`}
              disabled={!template || busy}
              onClick={() => setStep(step + 1)}
            >
              Next
            </button>
          ) : (
            <button
              className={`${studioButton} bg-primary text-primary-foreground hover:bg-primary/90`}
              disabled={!confirmed || busy}
              onClick={() => void render()}
            >
              {busy ? 'Submitting…' : 'Render private draft'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
